/**
 * AI Selection Registry — the system-level lookup that answers "what has the
 * user selected right now, and where?" for AI surfaces like Cmd+K.
 *
 * Pull-based by design: apps register a `getSelection()` getter once at mount
 * and it is only ever called at the moment an AI surface needs it. Nothing is
 * pushed on every keystroke or mouse drag, so an app can adopt this without
 * paying for it during normal editing, and a snapshot can never be stale.
 *
 * Debug: append ?debug_ai_selection=1 (or localStorage.debug_ai_selection='1')
 * for verbose [AI_SELECTION] logs and window.__aiSelection.dump().
 */

import { createFileLogger } from '../../utils/logger';
import { entityRegistry } from '../../stores/entityRegistry';
import useSpaceStore from '../../stores/spaceStore';
import type { AISelectionSource, ResolvedAISelection } from './types';

const log = createFileLogger('AI_SELECTION');

const DEBUG =
  typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).has('debug_ai_selection') ||
    localStorage.getItem('debug_ai_selection') === '1');

function debugLog(...args: any[]) {
  if (DEBUG) log.log('[aiSelection:debug]', ...args);
}

const sources = new Map<string, AISelectionSource>();
let nextSourceId = 1;

/**
 * Register a selection source. Returns its unregister function — callers should
 * treat that as the only way to remove it (ids are internal).
 */
export function registerAISelectionSource(source: AISelectionSource): () => void {
  const sourceId = `ais-${nextSourceId++}`;
  sources.set(sourceId, source);
  debugLog('registered', { sourceId, windowId: source.windowId, entityId: source.entityId });
  return () => {
    // An unmounting source can't be asked to clean up its own highlight later,
    // so drop it now while the callback is still live.
    if (highlightedSourceId === sourceId) releaseAISelectionHighlight();
    sources.delete(sourceId);
    debugLog('unregistered', { sourceId });
  };
}

let highlightedSourceId: string | null = null;

/**
 * Ask a source to hold its selection visible, because an AI surface is about to
 * take focus away from it. Releases whichever source held the highlight before.
 *
 * Ownership lives here rather than with each caller because the highlight is
 * global state — two AI surfaces must not be able to strand a highlight on a
 * source nothing is pointing at anymore.
 */
export function holdAISelectionHighlight(sourceId: string): void {
  if (highlightedSourceId === sourceId) return;
  releaseAISelectionHighlight();

  highlightedSourceId = sourceId;
  try {
    sources.get(sourceId)?.setSelectionHighlight?.(true);
    debugLog('highlight held', { sourceId });
  } catch (error) {
    log.warn('[aiSelection] source threw while painting highlight:', sourceId, error);
  }
}

/**
 * Drop the held highlight. `restoreFocus` hands the surface its focus back,
 * which is what makes a dismissed AI surface feel like it was never there: the
 * app repaints its own native selection and the user is left exactly where they
 * were. Skip it when something else is claiming focus next (a submitted prompt
 * moves the user to the chat input).
 */
export function releaseAISelectionHighlight(options?: { restoreFocus?: boolean }): void {
  if (!highlightedSourceId) return;

  const source = sources.get(highlightedSourceId);
  try {
    source?.setSelectionHighlight?.(false);
  } catch (error) {
    log.warn('[aiSelection] source threw while clearing highlight:', highlightedSourceId, error);
  }

  if (options?.restoreFocus && source) {
    // Focusing a contenteditable surface makes it re-render its own selection
    // from state, so the native highlight replaces ours with no flicker.
    try {
      safeGetElement(source)?.focus();
    } catch (error) {
      log.warn('[aiSelection] source threw while restoring focus:', highlightedSourceId, error);
    }
  }

  debugLog('highlight released', { sourceId: highlightedSourceId, restoreFocus: !!options?.restoreFocus });
  highlightedSourceId = null;
}

/** Every registered source, for tests and the debug dump. */
export function getAISelectionSourceCount(): number {
  return sources.size;
}

function safeGetSelection(sourceId: string, source: AISelectionSource) {
  try {
    return source.getSelection();
  } catch (error) {
    // A broken provider must never take down the Cmd+K path — skip it and let
    // the next tier resolve.
    log.warn('[aiSelection] source threw while reading selection:', sourceId, error);
    return null;
  }
}

function safeGetElement(source: AISelectionSource): HTMLElement | null {
  try {
    return source.getElement?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * Resolve the entity behind a source. Prefers the source's own entityId (apps
 * know exactly which entity they render) and falls back to the window's entity.
 */
/**
 * Whether a window counts as focused. A window inside a group shell is focused
 * when the shell itself is focused AND this is its visible tab — `activeWindowId`
 * points at the content-less shell, not the tab, so a bare equality check would
 * miss every grouped window.
 */
function isWindowFocused(windowId: string, activeWindowId: string | null): boolean {
  if (!activeWindowId) return false;
  if (windowId === activeWindowId) return true;
  const windows = useSpaceStore.getState().windows as Record<string, any>;
  const win = windows[windowId];
  if (win?.isTab && win.parentWindowId && win.parentWindowId === activeWindowId) {
    return windows[win.parentWindowId]?.activeTabId === windowId;
  }
  return false;
}

function resolveEntity(source: AISelectionSource) {
  if (source.entityId) {
    const entity = entityRegistry.findByAnyId(source.entityId) as any;
    if (entity) return entity;
  }
  if (source.windowId) {
    return useSpaceStore.getState().getWindowEntity(source.windowId) as any;
  }
  return null;
}

/**
 * Find the live selection an AI surface should act on, or null when nothing is
 * selected anywhere.
 *
 * Resolution is tiered, most-trustworthy first:
 *  1. `focus` — the source whose DOM subtree owns document.activeElement. This
 *     is how a real editor decides, and it's the only tier that disambiguates
 *     two windows on the same entity or an embed nested inside another editor.
 *  2. `activeWindow` — sources belonging to the focused window, for surfaces
 *     that hold a selection without a focused element (e.g. a Sheets range).
 *  3. `only` — a single registered source with a selection. Skipped when more
 *     than one source is live, since guessing between them would be worse than
 *     declining.
 */
export function resolveActiveAISelection(): ResolvedAISelection | null {
  if (sources.size === 0) return null;

  const entries = Array.from(sources.entries());
  const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
  const activeWindowId = useSpaceStore.getState().activeWindowId;

  const focusTier = activeElement
    ? entries.filter(([, source]) => {
        const el = safeGetElement(source);
        return !!el && (el === activeElement || el.contains(activeElement));
      })
    : [];

  const activeWindowTier = activeWindowId
    ? entries.filter(([, source]) => !!source.windowId && isWindowFocused(source.windowId, activeWindowId))
    : [];

  const onlyTier = entries.length === 1 ? entries : [];

  const tiers: Array<[ResolvedAISelection['resolvedBy'], typeof entries]> = [
    ['focus', focusTier],
    ['activeWindow', activeWindowTier],
    ['only', onlyTier],
  ];

  for (const [resolvedBy, tier] of tiers) {
    for (const [sourceId, source] of tier) {
      const snapshot = safeGetSelection(sourceId, source);
      if (!snapshot || !snapshot.text.trim()) continue;

      const entity = resolveEntity(source);
      const resolved: ResolvedAISelection = {
        ...snapshot,
        sourceId,
        resolvedBy,
        windowId: source.windowId,
        entityId: source.entityId ?? entity?._id ?? entity?.id,
        entityName: entity?.name,
        entityType: entity?.entityType,
        entityLlmId: entity?.llmId,
      };
      debugLog('resolved', {
        sourceId,
        resolvedBy,
        kind: resolved.kind,
        chars: resolved.text.length,
        entityName: resolved.entityName,
      });
      return resolved;
    }
  }

  debugLog('no live selection', { sources: sources.size, activeWindowId });
  return null;
}

if (DEBUG && typeof window !== 'undefined') {
  (window as any).__aiSelection = {
    dump: () => ({
      sources: Array.from(sources.entries()).map(([sourceId, source]) => ({
        sourceId,
        windowId: source.windowId,
        entityId: source.entityId,
        hasElement: !!safeGetElement(source),
        selection: safeGetSelection(sourceId, source),
      })),
      activeWindowId: useSpaceStore.getState().activeWindowId,
      resolved: resolveActiveAISelection(),
    }),
    resolve: resolveActiveAISelection,
  };
}
